import { useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { notification } from 'antd';

import { getCheckLock, postClearUserRecordLocks } from 'queries';
import { JsonDataItem, RecordLockType } from 'types';
import { parseErrors, Toast } from 'helpers';

type CheckLockProps = {
  recordId?: number;
  recordLockType: RecordLockType;
};

export const useCheckLock = ({ recordId, recordLockType }: CheckLockProps) => {
  const checkInterval = 10000; // every 10 seconds
  const notificationId = 'lock-message';
  const getMessage = (lockType: RecordLockType, lockedBy: string = 'another active user') => {
    switch (lockType) {
      case RecordLockType.Job:
        return `This job is currently locked by ${lockedBy}. You cannot modify it at this time.`;
      case RecordLockType.Customer:
        return `This customer file is currently locked by ${lockedBy}. You cannot modify it at this time.`;
      // specify for RecordLockType.Technician and RecordLockType.Quote when supported
      default:
        return `This record is currently locked by ${lockedBy}. You cannot modify it at this time.`;
    }
  };
  const {
    data,
    refetch: refetchLockStatus,
    isLoading,
  } = useQuery(['check-lock', { recordId, recordLockType }], getCheckLock, {
    enabled: !!recordId,
    refetchInterval: checkInterval,
    onSuccess: (data) => {
      if (data.isRecordLocked) {
        notification.open({
          closable: false,
          key: notificationId,
          message: ' ',
          description: getMessage(recordLockType, data.lockedBy),
          duration: 0,
          type: 'warning',
        });
      } else {
        notification.destroy(notificationId);
      }
    },
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  const { mutate: clearLocks } = useMutation(postClearUserRecordLocks, {
    onSuccess: () => notification.destroy(notificationId),
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  useEffect(() => {
    return () => {
      clearLocks();
    };
  }, []);

  return {
    isLocked: !!data?.isRecordLocked,
    isLoading,
    refetchLockStatus,
    clearLocks,
  };
};
